import React, { useRef } from "react";
import toast from "react-hot-toast";

import styles from "./styles.module.css";
import { handleApiError } from "../../utils/handleApiError";
import {
  addNoteTemplate,
  deleteNoteTemplate,
  updateNoteTemplate,
} from "../../services/location";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import Button from "../../components/button-v1";
import AlertDialog from "../../components/alert-dialogue";
export const AddNoteTemplateModal = ({ isOpen, location_id, onClose }) => {
  const queryClient = useQueryClient();
  const schema = z.object({
    note_template_text: z.string().trim().min(1, "Note template is required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { note_template_text: "" },
  });

  const { mutate, isLoading } = useMutation(
    ({ body, locationId }) => addNoteTemplate(body, locationId),
    {
      onSuccess: () => {
        toast.success("Note template added successfully");
        queryClient.invalidateQueries(["note_templates", location_id]);
        reset();
        onClose();
      },
      onError: (error) => handleApiError(error),
    }
  );

  const onSubmit = (data) => {
    mutate({
      body: { note_template_text: data.note_template_text },
      locationId: location_id,
    });
  };

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Note Template</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            className={styles.comp_wrapper}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>Note Template</label>
                <textarea
                  name="note_template_text"
                  placeholder="Enter note template text"
                  rows="6"
                  {...register("note_template_text")}
                />
                {errors.note_template_text && (
                  <span className="error-message">
                    {errors.note_template_text.message}
                  </span>
                )}
              </div>
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isLoading}>
            Add Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EditNoteTemplateModal = ({
  isOpen,
  onClose,
  location_id,
  noteTemplate,
}) => {
  const queryClient = useQueryClient();
  const schema = z.object({
    note_template_text: z.string().trim().min(1, "Note template is required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      note_template_text: noteTemplate?.note_template_text || "",
    },
  });

  const { mutate, isLoading } = useMutation(
    ({ noteTemplateId, body, locationId }) =>
      updateNoteTemplate(noteTemplateId, body, locationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["note_templates", location_id]);
        toast.success("Note template updated successfully");
        onClose();
      },
      onError: (error) => handleApiError(error),
    }
  );

  const onSubmit = (data) => {
    mutate({
      noteTemplateId: noteTemplate?.id,
      body: { note_template_text: data.note_template_text },
      locationId: location_id,
    });
  };

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Note Template</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            className={styles.comp_wrapper}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <label>Note Template</label>
                <textarea
                  name="note_template_text"
                  placeholder="Enter note template text"
                  rows="6"
                  {...register("note_template_text")}
                />
                {errors.note_template_text && (
                  <span className="error-message">
                    {errors.note_template_text.message}
                  </span>
                )}
              </div>
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isLoading}>
            Update Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DeleteNoteTemplateModal = ({
  isOpen,
  onClose,
  location_id,
  noteTemplateId,
}) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    ({ noteTemplateId, locationId }) =>
      deleteNoteTemplate(noteTemplateId, locationId),
    {
      onSuccess: () => {
        toast.success("Note template deleted successfully");
        queryClient.invalidateQueries(["note_templates", location_id]);
        onClose();
      },
      onError: (error) => handleApiError(error),
    }
  );

  const handleDelete = () => {
    mutate({ noteTemplateId, locationId: location_id });
  };

  return (
    <AlertDialog
      open={isOpen}
      setOpen={onClose}
      title="Delete Note Template"
      description="Are you sure you want to delete this note template?"
    >
      <Button variant="normal" size="default" onClick={onClose}>
        Cancel
      </Button>
      <Button
        loading={isLoading}
        variant="danger"
        size="default"
        onClick={handleDelete}
      >
        Delete
      </Button>
    </AlertDialog>
  );
};
